/**
 * TempoEngine.js
 * Generates humanized tempo maps with curves (Accell, Rit) and Rubato.
 */

const TEMPO_CONFIG = {
    phraseLengthBars: 4,
    ritardandoDurationBeats: 2,
    rubatoSteps: 4,
    baseRubatoDipBpm: 1.5
};

// Helper for fuzz
const fuzz = (value, variance, random) => {
    if (!random) return value;
    const range = variance * 2;
    return value + ((random.next() * range) - variance);
};

/**
 * Generates a Tempo Map (Array of tempo events).
 * @param {Array} structure - The song structure sections.
 * @param {number} baseBpm - The global BPM.
 * @param {Object} options - { humanize: boolean, random: object, getTension: function }
 * @returns {Array} - [{ tick: number, bpm: number }]
 */
export const generateTempoMap = (structure, baseBpm, options = {}) => {
    const map = [];
    const ticksPerBeat = 480;
    let currentTick = 0;
    const random = options.random;
    const getTension = options.getTension || (() => 0.5);

    // Initial Tempo
    map.push({ tick: 0, bpm: baseBpm });

    if (!options.humanize) return map;

    let barIndex = 0;

    structure.forEach((section, idx) => {
        const type = section.type.toUpperCase();
        const tension = getTension(barIndex);

        // Influence 1: Tension -> Rubato Depth
        // High Tension = Locked Tempo (Stability)
        // Low Tension = Loose (Rubato)
        // Invert tension: 1.0 -> 0.0 rubato, 0.0 -> 1.0 rubato
        let rubatoIntensity = options.rubatoIntensity !== undefined ? options.rubatoIntensity : 0.5;
        rubatoIntensity *= (1.0 - (tension * 0.8)); // Reduce rubato at high tension

        // Clamp min rubato to allow some breath even at high tension if user requested it
        rubatoIntensity = Math.max(0.1, rubatoIntensity);
        const durationBeats = section.bars * 4;
        const startTick = currentTick;
        const endTick = startTick + (durationBeats * ticksPerBeat);

        // 1. Structural Changes
        let targetBpm = baseBpm;

        if (type.includes('BUILD') || type.includes('RISE')) {
            // Accelerando
            const steps = 8;
            const boost = fuzz(16, 4, random);
            for(let i=0; i<steps; i++) {
                const t = startTick + Math.round((i/steps) * (endTick - startTick));
                const b = baseBpm + (i * (boost/steps));
                map.push({ tick: t, bpm: b });
            }
            targetBpm = baseBpm + boost;
        }
        else if (type.includes('OUTRO') || type.includes('ENDING')) {
            // Ritardando (last 2 bars)
            const ritStart = endTick - (2 * 4 * ticksPerBeat);
            const steps = 8;
            const drag = fuzz(32, 8, random);
            for(let i=0; i<steps; i++) {
                const t = ritStart + Math.round((i/steps) * (endTick - ritStart));
                if (t > startTick) {
                    const b = baseBpm - (i * (drag/steps));
                    map.push({ tick: t, bpm: b });
                }
            }
        }
        else if (type.includes('SLOW') || type.includes('ADAGIO')) {
            map.push({ tick: startTick, bpm: baseBpm * 0.8 });
        }
        else {
            map.push({ tick: startTick, bpm: baseBpm });
        }

        // 2. Phrase-End Rubato (Enhancement #4)
        // Insert mini-ritardando at the end of every phrase
        if (rubatoIntensity > 0.05) {
            const effectiveDip = TEMPO_CONFIG.baseRubatoDipBpm * (rubatoIntensity * 2.0); // Scale dip

            for (let b = TEMPO_CONFIG.phraseLengthBars; b <= section.bars; b += TEMPO_CONFIG.phraseLengthBars) {
                const phraseEndTick = startTick + (b * 4 * ticksPerBeat);
                const ritStart = phraseEndTick - (TEMPO_CONFIG.ritardandoDurationBeats * ticksPerBeat);

                // Only apply if section is melodic/expressive
                if (type.includes('VERSE') || type.includes('CHORUS') || type.includes('INTRO')) {
                    for(let i=0; i<TEMPO_CONFIG.rubatoSteps; i++) {
                        const t = ritStart + Math.round((i/TEMPO_CONFIG.rubatoSteps) * (phraseEndTick - ritStart));
                        const dip = (i+1) * effectiveDip;
                        map.push({ tick: t, bpm: baseBpm - dip });
                    }
                    // Snap back at downbeat
                    map.push({ tick: phraseEndTick, bpm: baseBpm });
                }
            }
        }

        currentTick = endTick;
        barIndex += section.bars;
    });

    // Cleanup: Sort by tick
    map.sort((a,b) => a.tick - b.tick);

    // Remove duplicates (same tick) - keep last
    const unique = [];
    map.forEach(ev => {
        const last = unique[unique.length-1];
        if (last && last.tick === ev.tick) {
            unique[unique.length-1] = ev;
        } else {
            unique.push(ev);
        }
    });

    return unique;
};
